import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FileSpreadsheet, Download, AlertTriangle } from 'lucide-react';
import Modal from './Modal';

interface MasavUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Hands the validated file + selected month off to the next stage (prompt 2).
  onContinue: (payload: { file: File; selectedMonth: string }) => void; // selectedMonth = "YYYY-MM"
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Exact column headers required for the Masav payments template, in order.
const CSV_HEADERS = [
  'שם לקוח',
  'זהות',
  'כתובת',
  'טלפון',
  'פרטי חשבון',
  'סכום',
  'יתרת תשלומים',
  'קטגוריה',
  'הערות',
];

const Instructions = styled.div`
  background: var(--info-light);
  color: var(--info-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--info-color);
`;

const InstructionsTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const InstructionsList = styled.ul`
  margin: 0;
  padding-right: 20px;

  li {
    margin-bottom: 4px;
    font-size: 14px;
  }
`;

const TemplateRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const UploadSection = styled.div<{ $isDragOver: boolean }>`
  text-align: center;
  padding: 40px 20px;
  border: 2px dashed ${props => (props.$isDragOver ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  cursor: pointer;
  background: ${props => (props.$isDragOver ? 'var(--primary-light)' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    background: var(--primary-light);
  }
`;

const UploadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  margin-bottom: 16px;
`;

const UploadText = styled.div`
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const FileInput = styled.input`
  display: none;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedFile = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  font-size: 18px;
  padding: 4px;

  &:hover {
    background: var(--error-light);
    border-radius: var(--border-radius-sm);
  }
`;

const MonthSection = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const MonthInput = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const MonthWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: var(--warning-light);
  color: var(--warning-color);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
`;

const ErrorsList = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  margin-bottom: 16px;
`;

const ErrorItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  color: var(--error-color);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Current month as "YYYY-MM" (default fallback when filename parsing fails).
const currentMonth = (): string => new Date().toISOString().slice(0, 7);

// Parses month/year from a filename like "DohMasav250526" where the trailing
// digits are DDMMYY. Returns "YYYY-MM", or null if no valid match is found.
const parseMonthFromFilename = (fileName: string): string | null => {
  // Strip extension
  const base = fileName.replace(/\.[^.]+$/, '');
  // Find a 6-digit run at the very end of the base name
  const match = base.match(/(\d{6})(?!.*\d)/);
  if (!match) return null;

  const digits = match[1];
  const month = digits.slice(2, 4); // ignore DD (first 2 digits)
  const yy = digits.slice(4, 6);

  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) return null;

  const year = 2000 + parseInt(yy, 10);
  return `${year}-${month}`;
};

const MasavUploadModal: React.FC<MasavUploadModalProps> = ({
  isOpen,
  onClose,
  onContinue,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // "YYYY-MM"
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | undefined | null) => {
    if (!file) return;

    // Validate file type (CSV only)
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('יש לבחור קובץ CSV בלבד. נא לוודא שהקובץ נשמר עם סיומת .csv');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > MAX_FILE_SIZE) {
      setError(`גודל הקובץ (${formatFileSize(file.size)}) גדול מדי. הגודל המקסימלי המותר הוא 10MB`);
      return;
    }

    // Validate file is not empty
    if (file.size === 0) {
      setError('הקובץ שנבחר ריק. נא לבחור קובץ עם נתונים');
      return;
    }

    setError(null);
    setSelectedFile(file);
    // Re-run month parsing on every (re)selection
    setSelectedMonth(parseMonthFromFilename(file.name) ?? currentMonth());
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setSelectedMonth('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // Header row only; BOM ensures Hebrew renders correctly in Excel
      const csvContent = CSV_HEADERS.join(',');
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'masav_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Error downloading template file:', err);
      setError('שגיאה בהורדת התבנית. נסה שוב');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedMonth('');
    setError(null);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleContinue = () => {
    if (!selectedFile || !selectedMonth) return;
    // TODO (prompt 2): hand off { file, selectedMonth } to the Masav validation/matching engine.
    onContinue({ file: selectedFile, selectedMonth });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='העלאת קובץ תשלומי מס"ב'
      width="700px"
      maxWidth="95vw"
    >
      <Instructions>
        <InstructionsTitle>הוראות לשימוש:</InstructionsTitle>
        <InstructionsList>
          <li>הורד את תבנית ה-CSV ומלא את פרטי התשלומים בעמודות הנתונות.</li>
          <li>ניתן לשמור את הקובץ מ-Excel בכל פורמט נפוץ (CSV או טקסט מופרד טאבים, בקידוד UTF-8 או Unicode).</li>
          <li>שם הקובץ הצפוי כולל תאריך בסוף בפורמט DDMMYY (לדוגמה: DohMasav250526).</li>
          <li>גודל הקובץ המקסימלי: 10MB.</li>
        </InstructionsList>
      </Instructions>

      <TemplateRow>
        <Button onClick={handleDownloadTemplate}>
          <Download size={16} />
          הורדת תבנית CSV
        </Button>
      </TemplateRow>

      {error && (
        <ErrorsList>
          <ErrorItem>{error}</ErrorItem>
        </ErrorsList>
      )}

      {!selectedFile ? (
        <UploadSection
          $isDragOver={isDragOver}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          <UploadIcon><FileSpreadsheet size={48} strokeWidth={1.5} /></UploadIcon>
          <UploadText>לחץ לבחירת קובץ CSV</UploadText>
          <UploadSubtext>או גרור ושחרר קובץ כאן</UploadSubtext>
          <FileInput
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
          />
        </UploadSection>
      ) : (
        <>
          <SelectedFile>
            <FileInfo>
              <FileName><FileSpreadsheet size={18} strokeWidth={2} />{selectedFile.name}</FileName>
              <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
            </FileInfo>
            <RemoveButton onClick={handleRemoveFile} title="הסר קובץ">×</RemoveButton>
          </SelectedFile>

          <MonthSection>
            <Label>חודש הדיווח</Label>
            <MonthInput
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
            <MonthWarning>
              <AlertTriangle size={16} />
              יש לוודא שהחודש תקין לפני המשך
            </MonthWarning>
          </MonthSection>
        </>
      )}

      <ButtonGroup>
        <Button onClick={handleClose}>ביטול</Button>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={!selectedFile || !selectedMonth}
        >
          המשך
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

export default MasavUploadModal;
